import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import YouTubePlayer from "@/components/education/YouTubePlayer";
import api from "@/api";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, CheckCircle, MessageCircle, HelpCircle,
  ChevronRight, BookOpen, ListChecks, Heart, Utensils, 
  AlertTriangle, Smile, Sparkles, Stethoscope, Activity, Send
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Category configuration (icons/colors only — labels come from i18n)
const CATEGORY_CONFIG = {
  BIENVENUE: { icon: Sparkles, color: "emerald" },
  TRAITEMENT: { icon: Stethoscope, color: "blue" },
  NUTRITION: { icon: Utensils, color: "orange" },
  EFFETS_SECONDAIRES: { icon: Activity, color: "purple" },
  SIGNES_ALERTE: { icon: AlertTriangle, color: "red" },
  BIEN_VIVRE: { icon: Smile, color: "teal" },
  GESTION_SYMPTOMES: { icon: Heart, color: "pink" },
};

const getCategoryConfig = (category) => {
  return CATEGORY_CONFIG[category] || { icon: BookOpen, color: "slate" };
};

export default function EducationContentDetail() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [understood, setUnderstood] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [sendingQuestion, setSendingQuestion] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [contentId]);

  const fetchContent = async () => {
    try {
      const res = await api.get(`/education/content/${contentId}`);
      setContent(res.data);

      // Check if there's a post-questionnaire
      if (res.data.hasPostQuestionnaire) {
        try {
          const qRes = await api.get(`/education/content/${contentId}/questionnaire?stage=AFTER`);
          if (qRes.data) {
            setQuestionnaire(qRes.data);
          }
        } catch (e) {
          // No questionnaire available
        }
      }
    } catch (err) {
      // error handled by toast
      toast.error(t("education.loadContentError"));
      navigate("/patient/education");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoStart = async () => {
    try {
      await api.post(`/education/content/${contentId}/track`, {
        interactionType: "VIDEO_STARTED"
      });
    } catch (e) {
      // error handled
    }
  };

  const handleVideoEnd = async () => {
    try {
      await api.post(`/education/content/${contentId}/track`, {
        interactionType: "VIDEO_COMPLETED"
      });
    } catch (e) {
      // error handled
    }
  };

  const handleUnderstood = async () => {
    try {
      await api.post(`/education/content/${contentId}/track`, {
        interactionType: "UNDERSTOOD"
      });
      setUnderstood(true);
      toast.success(t("education.understandRecorded"));
      
      // Show questionnaire if available
      if (questionnaire) {
        setShowQuestionnaire(true);
      }
    } catch (e) {
      // error handled
      toast.error(t("education.recordError"));
    }
  };

  const handleSendQuestion = async () => {
    if (!questionText.trim()) {
      toast.error(t("education.pleaseTypeQuestion"));
      return;
    }

    setSendingQuestion(true);
    try {
      await api.post(`/education/content/${contentId}/question`, {
        messageText: questionText.trim()
      });
      toast.success(t("education.questionSent"));
      setShowQuestionModal(false);
      setQuestionText("");
    } catch (e) {
      // error handled
      toast.error(t("education.sendQuestionError"));
    } finally {
      setSendingQuestion(false);
    }
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </PatientLayout>
    );
  }

  if (!content) {
    return (
      <PatientLayout>
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t("education.contentNotFound")}</p>
        </div>
      </PatientLayout>
    );
  }

  const config = getCategoryConfig(content.category);
  const Icon = config.icon;
  const isVideo = content.contentType === "VIDEO" && content.youtubeVideoId;
  const duration = content.durationSeconds 
    ? `${Math.round(content.durationSeconds / 60)} min`
    : null;

  return (
    <PatientLayout>
      <div className="space-y-5 pb-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/patient/education")}
          className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
          data-testid="education-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">{t("education.backToEducation")}</span>
        </button>

        {/* Category badge */}
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 text-sm font-medium rounded-full bg-${config.color}-100 text-${config.color}-700 flex items-center gap-1.5`}>
            <Icon className="w-4 h-4" />
            {t(`education.categories.${content.category}`)}
          </span>
          {duration && (
            <span className="px-2 py-1 text-xs text-slate-500 bg-slate-100 rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {duration}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-slate-800">{content.title}</h1>

        {/* Video player */}
        {isVideo && (
          <div className="animate-fade-in-up">
            <YouTubePlayer
              videoId={content.youtubeVideoId}
              title={content.title}
              onVideoStart={handleVideoStart}
              onVideoEnd={handleVideoEnd}
              className="shadow-lg"
            />
          </div>
        )}

        {/* Description */}
        {content.bodyText && (
          <div className="bg-white rounded-2xl p-5 border border-slate-200 animate-fade-in-up stagger-1">
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">
              {content.bodyText}
            </p>
          </div>
        )}

        {/* Key points */}
        {content.keyPoints && content.keyPoints.length > 0 && (
          <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200 animate-fade-in-up stagger-2">
            <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
              <ListChecks className="w-5 h-5" />
              {t("education.keyPoints")}
            </h3>
            <ul className="space-y-2">
              {content.keyPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-2 text-emerald-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3 animate-fade-in-up stagger-3">
          <p className="text-center text-slate-600 font-medium">
            {t("education.askComprehension")}
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {/* J'ai compris button */}
            <button
              onClick={handleUnderstood}
              disabled={understood}
              data-testid="education-understood-btn"
              className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl font-semibold transition-all ${
                understood
                  ? "bg-green-100 text-green-700 border-2 border-green-300"
                  : "bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95"
              }`}
            >
              <CheckCircle className="w-8 h-8" />
              <span>{understood ? t("education.understoodThanks") : t("education.understood")}</span>
            </button>

            {/* J'ai une question button */}
            <button
              onClick={() => setShowQuestionModal(true)}
              data-testid="education-question-btn"
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all active:scale-95"
            >
              <HelpCircle className="w-8 h-8" />
              <span>{t("education.askQuestion")}</span>
            </button>
          </div>

          {/* Questionnaire button if available */}
          {questionnaire && (
            <button
              onClick={() => setShowQuestionnaire(true)}
              data-testid="education-questionnaire-btn"
              className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl font-semibold bg-blue-50 text-blue-700 border-2 border-blue-200 hover:bg-blue-100 transition-all"
            >
              <ListChecks className="w-6 h-6" />
              <span>{t("education.startQuestionnaire")}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Question Modal */}
      <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              {t("education.questionModalTitle")}
            </DialogTitle>
            {t("education.questionModalDesc") && (
              <DialogDescription>
                {t("education.questionModalDesc")}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600">
              <strong>{t("education.contentLabel")} :</strong> {content.title}
            </div>
            
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder={t("education.questionPlaceholder")}
              className="w-full h-32 p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              data-testid="education-question-input"
            />

            <button
              onClick={handleSendQuestion}
              disabled={sendingQuestion || !questionText.trim()}
              className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="education-send-question-btn"
            >
              {sendingQuestion ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("education.sendingQuestion")}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t("education.sendQuestion")}
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Questionnaire Modal */}
      <QuestionnaireModal
        open={showQuestionnaire}
        onClose={() => setShowQuestionnaire(false)}
        questionnaire={questionnaire}
        contentId={contentId}
      />
    </PatientLayout>
  );
}

// Questionnaire Modal Component
function QuestionnaireModal({ open, onClose, questionnaire, contentId }) {
  const { t } = useLanguage();
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!questionnaire) return null;

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
      questionId,
      answer
    }));

    setSubmitting(true);
    try {
      const res = await api.post(
        `/education/content/${contentId}/questionnaire/${questionnaire.id}/submit`,
        { answers: formattedAnswers }
      );
      setResult(res.data);
      toast.success(t("education.questionnaireCompleted"));
    } catch (e) {
      // error handled
      toast.error(t("education.questionnaireSubmitError"));
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = questionnaire.questions.every(
    q => !q.required || answers[q.id] !== undefined
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-blue-600" />
            {questionnaire.title}
          </DialogTitle>
          {questionnaire.description && (
            <DialogDescription>{questionnaire.description}</DialogDescription>
          )}
        </DialogHeader>

        {result ? (
          // Result view
          <div className="space-y-4 mt-4">
            <div className={`p-6 rounded-2xl text-center ${
              result.interpretation === "GOOD" ? "bg-green-50" :
              result.interpretation === "NEEDS_REVIEW" ? "bg-yellow-50" : "bg-red-50"
            }`}>
              <div className={`text-4xl font-bold ${
                result.interpretation === "GOOD" ? "text-green-600" :
                result.interpretation === "NEEDS_REVIEW" ? "text-yellow-600" : "text-red-600"
              }`}>
                {result.score}%
              </div>
              <p className="mt-2 font-medium text-slate-700">
                {result.correctAnswers}/{result.totalQuestions} {t("education.correctAnswers")}
              </p>
            </div>
            
            <p className="text-center text-slate-600">
              {result.interpretation === "GOOD" 
                ? t("education.questionnaireGood")
                : result.interpretation === "NEEDS_REVIEW"
                ? t("education.questionnaireNeedsReview")
                : t("education.questionnairePoor")}
            </p>

            <button
              onClick={onClose}
              className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200"
            >
              {t("education.closeBtn")}
            </button>
          </div>
        ) : (
          // Questions view
          <div className="space-y-6 mt-4">
            {questionnaire.questions.map((question, idx) => (
              <div key={question.id} className="space-y-3">
                <p className="font-medium text-slate-800">
                  {idx + 1}. {question.text}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </p>
                
                {question.type === "SINGLE_CHOICE" && (
                  <div className="space-y-2">
                    {question.options.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleAnswer(question.id, option)}
                        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                          answers[question.id] === option
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}

                {question.type === "MULTIPLE_CHOICE" && (
                  <div className="space-y-2">
                    {question.options.map((option) => {
                      const currentAnswers = answers[question.id] || [];
                      const isSelected = currentAnswers.includes(option);
                      
                      return (
                        <button
                          key={option}
                          onClick={() => {
                            const newAnswers = isSelected
                              ? currentAnswers.filter(a => a !== option)
                              : [...currentAnswers, option];
                            handleAnswer(question.id, newAnswers);
                          }}
                          className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                            isSelected
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? "border-emerald-500 bg-emerald-500" : "border-slate-300"
                            }`}>
                              {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                            </span>
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("education.sendingQuestionnaire")}
                </>
              ) : (
                t("education.submitAnswers")
              )}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
